import colors from "shared/styles/colors";
import { IVariantProps, ThemeType } from "shared/styles/theme";
import styled, { css } from "styled-components";
import theme from "styled-theming";
import { lighten } from "polished";
import { inputDisabledBackgroundColor } from "./Input";

export enum ExpressionEditorContainerState {
    primary = "primary",
    error = "error"
}

export enum OpenExternalLinkColorVariant {
    purple = "purple",
    white = "white"
}

const expressionEditorContainerBorderColor = theme.variants("mode", "variant", {
    [ExpressionEditorContainerState.primary]: {
        [ThemeType.light]: colors.grey_2,
        [ThemeType.dark]: colors.grey_2
    },
    [ExpressionEditorContainerState.error]: {
        [ThemeType.light]: colors.warningDark,
        [ThemeType.dark]: colors.warningDark
    }
})

const openExternalLinkColor = theme.variants("mode", "variant", {
    [OpenExternalLinkColorVariant.purple]: {
        [ThemeType.light]: colors.purplePrimary,
        [ThemeType.dark]: colors.purpleTertiary
    },
    [OpenExternalLinkColorVariant.white]: {
        [ThemeType.light]: colors.white,
        [ThemeType.dark]: colors.white
    }
})

const expressionEditorContainerBackgroundColor = theme.variants("mode", "variant", {
    [ExpressionEditorContainerState.primary]: {
        [ThemeType.light]: colors.white,
        [ThemeType.dark]: colors.dark2
    },
    [ExpressionEditorContainerState.error]: {
        [ThemeType.light]: colors.white,
        [ThemeType.dark]: colors.dark2
    }
})

const SharedParameterFieldsetStyles = css`
    border: none;
    min-width: 0;
    margin: 0;

    hr {
        margin: 16px 0;
    }
`

export const ParameterFieldset = styled.fieldset<{ $hasYPadding?: boolean }>`
    ${SharedParameterFieldsetStyles};
    padding: ${props => props.$hasYPadding ? "24px" : 0} 24px;
`

export const InnerParameterFieldset = styled.fieldset`
    ${SharedParameterFieldsetStyles};
    padding: 0;
`

export const ExpressionEditorContainer = styled.div<IVariantProps<ExpressionEditorContainerState>>`
    border: 2px solid ${expressionEditorContainerBorderColor};
    border-radius: 20px;
    display: block;
    width: 100%;
    padding: 8px 12px;
    height: auto !important;
    display: block;
    background-color: ${expressionEditorContainerBackgroundColor};

    button {
        all: unset;
        cursor: pointer;

        i {
            font-size: var(--header-4-font-size);
        }
    }

    &:has(.monaco-editor-selector.readonly) { // custom class names set in MonacoEditor.tsx
        background-color: ${inputDisabledBackgroundColor};
        border-color: ${inputDisabledBackgroundColor};

        .monaco-editor {
            .margin, .view-lines, .monaco-editor-background {
                background-color: ${inputDisabledBackgroundColor} !important;
            }
        }
    }
`;

export const ParameterViewerStyled = styled.div`
    overflow: auto;
`

export const OpenExternalLink = styled.a<IVariantProps<OpenExternalLinkColorVariant>>`
    color: ${openExternalLinkColor};
    font-family: "Gilroy-Medium", "medium-fallback";
    font-size: var(--header-4-font-size);
    display: flex;
    align-items: center;
    gap: 4px;
    margin-bottom: 10px;
    cursor: pointer;
`

export const ProcessElementParameterHeader = styled.div<{ $isPanelPinned?: boolean }>`
    padding: 24px;
    ${props => props.$isPanelPinned && css`
        background-color: inherit;
    `}
`

export enum ElementRowVariant {
    primary = "primary",
    selected = "selected"
}

const elementRowSelectedBackgroundColors = {
    [ThemeType.light]: colors.purpleSecondary,
    [ThemeType.dark]: colors.dark3
}

const elementRowBackgroundColor = theme.variants("mode", "variant", {
    [ElementRowVariant.primary]: {
        [ThemeType.light]: colors.white,
        [ThemeType.dark]: colors.dark2
    },
    [ElementRowVariant.selected]: {
        [ThemeType.light]: elementRowSelectedBackgroundColors[ThemeType.light],
        [ThemeType.dark]: elementRowSelectedBackgroundColors[ThemeType.dark]
    }
})

const elementRowBoxShadow = theme.variants("mode", "variant", {
    [ElementRowVariant.primary]: {
        [ThemeType.light]: "none",
        [ThemeType.dark]: "none"
    },
    [ElementRowVariant.selected]: {
        [ThemeType.light]: "0px 0px 4px rgba(0, 0, 0, 0.25);",
        [ThemeType.dark]: "none"
    }
})

const elementRowHeaderContainerHover = theme("mode", {
    [ThemeType.light]: colors.purpleBackground,
    [ThemeType.dark]: colors.dark3
})

interface ElementRowProps {
    variant: ElementRowVariant;
    $level?: number;
}

const lightenColorByScale = 0.05;

export const ElementRowStyled = styled.div<ElementRowProps>`
    background-color: ${(props) => {
        const baseColor = elementRowBackgroundColor(props);
        return props.$level ? lighten(props.$level * lightenColorByScale, baseColor) : baseColor;
    }};
    border-radius: 25px;
    box-shadow: ${elementRowBoxShadow};
    margin: 10px;
`


interface ElementArrayObjectProps {
    $level?: number;
}

export const ElementArrayObject = styled.tr<ElementArrayObjectProps>`
    & > td > div {
        padding: 5px;
        border-radius: 15px;
        background-color: ${(props) => {
        const baseColor = elementRowSelectedBackgroundColors[props.theme.mode as ThemeType];
        return props.$level ? lighten(props.$level * lightenColorByScale, baseColor) : baseColor;
    }};
    }
`;

export const ElementHeaderContainer = styled.div`
    padding: 0 20px;
    height: 50px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-radius: 25px;
    label {
        margin-bottom: 0;
    }
    &:hover {
        background-color: ${elementRowHeaderContainerHover};
    }
`

export const ElementContentContainer = styled.div`
    padding: 0 20px;
    hr {
        border: 1px solid ${colors.purpleTertiary};
        margin: 0px 0 25px 0;
    }
`

ExpressionEditorContainer.defaultProps = {
    variant: ExpressionEditorContainerState.primary
}

OpenExternalLink.defaultProps = {
    variant: OpenExternalLinkColorVariant.purple
}